import {openDeleteDialog, openDialog, showNotification} from 'platform/components';
import {HStack, Icon, Text, VStack} from 'platform/foundation';

import {useState} from 'react';

import {equals} from 'ramda';

import {
  CampaignStatusSchema,
  ListCampaignResponseBody,
  useDeleteCampaignMutation,
  useReorderCampaignsMutation,
} from '@dms/api/sales';
import {SortableCardList} from '@dms/features/dnd';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsSection} from '@dms/settings/shared';
import {handleApiError} from '@dms/shared';

import {composePath, RequiredTestIdProps, suffixTestId, useNavigate} from 'shared/web';

import {openRepublishAdsConfirmDialog} from '../utils/openRepublishAdsConfirmDialog';
import {CampaignCard} from './CampaignCard';
import {CampaignPublishingDeleteContent} from './CampaignPublishingDeleteContent';

const CONTAINER_ID = 'campaigns';
const FIRST_POSITION = 1;
const DELETE_CAMPAIGN_PUBLISHING_DIALOG_ID = 'deleteCampaignPublishingDialog';

const getActiveCampaignIds = (campaigns: ListCampaignResponseBody[]) =>
  campaigns
    .filter((campaign) => campaign.status === CampaignStatusSchema.enum.ACTIVE)
    .map((campaign) => campaign.id);

interface CampaignListProps extends RequiredTestIdProps {
  campaigns: ListCampaignResponseBody[];
}

export function CampaignList(props: CampaignListProps) {
  const navigate = useNavigate();
  const [pendingCampaigns, setPendingCampaigns] = useState<ListCampaignResponseBody[] | null>(null);
  const [reorderCampaigns, {isLoading: isReorderingCampaignsLoading}] =
    useReorderCampaignsMutation();
  const [deleteCampaign] = useDeleteCampaignMutation();

  const handleEdit = (campaignId: string) =>
    navigate(composePath(settingsRoutes.campaignEdit, {params: {id: campaignId}}));

  const handleReorder = (reorderedCampaigns: ListCampaignResponseBody[]) => {
    setPendingCampaigns(reorderedCampaigns);

    const hasActiveCampaignOrderChanged = !equals(
      getActiveCampaignIds(props.campaigns),
      getActiveCampaignIds(reorderedCampaigns)
    );

    const reorder = (republishAds: boolean) =>
      reorderCampaigns({
        reorderCampaignsRequestBody: {
          positions: reorderedCampaigns.map((campaign, index) => ({
            campaignId: campaign.id,
            position: index + FIRST_POSITION,
          })),
          republishAds,
        },
      })
        .unwrap()
        .then(() => {
          showNotification.success(i18n.t('general.notifications.changesSuccessfullySaved'));
        })
        .catch(handleApiError)
        .finally(() => setPendingCampaigns(null));

    if (!hasActiveCampaignOrderChanged) {
      reorder(false);
      return;
    }

    void openRepublishAdsConfirmDialog({
      onSelect: reorder,
      onDismiss: () => setPendingCampaigns(null),
    });
  };

  const handleDelete = (campaign: ListCampaignResponseBody) => {
    openDeleteDialog({
      text: i18n.t('entity.salesCampaign.labels.deleteDialogText'),
      'data-testid': testIds.settings.campaigns('deleteCampaignConfirmDialog'),
      onConfirm: () => {
        deleteCampaign({
          id: campaign.id,
          republishAds: campaign.status === CampaignStatusSchema.enum.ACTIVE ? true : false,
        })
          .unwrap()
          .then(() => {
            showNotification.success(i18n.t('general.notifications.successfullyDeleted'));
            if (campaign.status === CampaignStatusSchema.enum.ACTIVE) {
              openDialog(<CampaignPublishingDeleteContent />, {
                id: DELETE_CAMPAIGN_PUBLISHING_DIALOG_ID,
                'data-testid': DELETE_CAMPAIGN_PUBLISHING_DIALOG_ID,
              });
            }
          })
          .catch(handleApiError);
      },
    });
  };

  return (
    <SettingsSection>
      <VStack spacing={4} align="flex-start">
        <SortableCardList<ListCampaignResponseBody>
          withHandle
          disableDropAnimation
          disabledDrag={isReorderingCampaignsLoading}
          containerId={CONTAINER_ID}
          items={pendingCampaigns ?? props.campaigns}
          onReorder={handleReorder}
          renderCard={(campaign, _index, handleProps) => (
            <CampaignCard
              campaign={campaign}
              handleProps={handleProps}
              onEdit={handleEdit}
              onDelete={handleDelete}
              data-testid={suffixTestId('list', props)}
            />
          )}
        />

        <HStack spacing={2} align="center">
          <Icon value="action/drag_indicator" size={4} color="palettes.neutral.60.100" />
          <Text size="small" color="tertiary" data-testid={suffixTestId('reorderHint', props)}>
            {i18n.t('entity.salesCampaign.labels.reorderHint')}
          </Text>
        </HStack>
      </VStack>
    </SettingsSection>
  );
}
